import React, { Fragment, useEffect, useState } from "react";
import { ReactComponent as SearchIcon } from "assets/images/common/svgs/search.svg";
import { ReactComponent as EyeIcon } from "assets/images/common/svgs/eyeIconTable.svg";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDebounce } from "hooks/useDebounce";
import DataNotFound from "components/particles/table/data-not-found";
import Pagination from "components/particles/table/pagination";
import useProductItem from "containers/master-portal/useHooks";
import { OrderRequestFilterDTO } from "utils/helpers/models/master-portal/order-request-filter.dto";
import { orderRequestDTO } from "utils/helpers/models/master-portal/order-request.dto";
import { ORDER_STATUS } from "utils/helpers/enums/roles.enum";
import useStore from "hooks/useStore";
import { sitePermissions } from "utils/helpers/enums/permissions.enum";

const OrderRequests: React.FC = () => {
  const { hasPermission } = useStore();
  const statuses = [
    { title: "Pending", value: ORDER_STATUS.PENDING },
    { title: "Dispatched", value: ORDER_STATUS.DISPATCHED },
  ];

  const navigate = useNavigate();
  const [data, setData] = useState<orderRequestDTO[]>([]);
  const { getOrderRequest } = useProductItem();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [filters, setFilters] = useState<OrderRequestFilterDTO>(
    new OrderRequestFilterDTO()
  );

  const [totalElements, setTotalElements] = useState<number>(0);

  const debounceSearch = useDebounce(filters.ambassadorName, 1000);

  const buildUpdatedFilters = (
    updates: Partial<OrderRequestFilterDTO> = {},
    resetPage: boolean = false
  ) => {
    const updatedFilters = { ...filters, ...updates };
    if (resetPage) {
      updatedFilters.page = 1;
    }
    return updatedFilters;
  };

  const columns: string[] = [
    "ID",
    "Ambassador Name",
    "Item",
    "Address",
    "Action",
  ];

  const onPageChange = (pageInfo: { selected: number; limit: number }) => {
    const { selected: page, limit } = pageInfo;
    const updatedFilters = buildUpdatedFilters(
      { limit: limit, page: page + 1 },
      false
    );
    setFilters(updatedFilters);
    getOrederRequestData(updatedFilters);
  };

  const getOrederRequestData = (filters: OrderRequestFilterDTO) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "" && key !== "statuses") {
        filtersToSend[key] = filters[key];
      }
    }
    const queryParams = {
      ...filtersToSend,
      page: filters.page - 1,
      status: filters.status,
    };
    getOrderRequest(setData, queryParams, setFilters, setTotalElements);
  };

  useEffect(() => {
    getOrederRequestData(filters);
  }, [debounceSearch]);

  // Initial load on component mount
  useEffect(() => {
    getOrederRequestData(filters);
  }, []);

  const handleChangeFilter = (event: any) => {
    const {
      target: { name, value },
    } = event;
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const updatedFilters = buildUpdatedFilters(
      { [name]: trimmedValue },
      true
    );
    setFilters(updatedFilters);
    if (name === "ambassadorName" && trimmedValue === "") {
      getOrederRequestData(updatedFilters);
    }
  };

  const resetFilters = () => {
    const updatedFilters = buildUpdatedFilters(new OrderRequestFilterDTO(), true);
    setFilters(updatedFilters);
    getOrederRequestData(updatedFilters);
  };

  const handleSearch = (event: any) => {
    const { key } = event;
    if (key === "Enter") {
      const updatedFilters = buildUpdatedFilters({}, true);
      setFilters(updatedFilters);
      getOrederRequestData(updatedFilters);
    }
  };

  const handleTabChange = (statusValue: string) => {
    const updatedFilters: any = { ...filters, status: statusValue, page: 1 };
    setFilters(updatedFilters);
    getOrederRequestData(updatedFilters);
  };

  const goToDetail = (orderRequest: orderRequestDTO) => {
    navigate(siteRoutes.viewOrderRequest, { state: { orderRequest } });
  };

  return (
    <div className="">
      <h1 className="tex-2xl sm:text-[32px] text-primaryText font-bold">
        Order Requests
      </h1>
      <div className="flex items-center gap-5 my-6">
        <div className="w-full sm:w-[388px] relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primaryText text-lg" />
          <input
            type="search"
            placeholder="Search by Ambassador Name"
            value={filters.ambassadorName}
            onChange={handleChangeFilter}
            onKeyUp={handleSearch}
            name="ambassadorName"
            className="w-full search-input pl-10 p-2 border-[0.6px] border-borderLightGray rounded-full focus:outline-none bg-coolLightGrayBg"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 justify-start mb-6">
        {statuses.map((item: any) => (
          <button
            key={item.value}
            type="button"
            className={`px-4 py-2 text-xs rounded-md transition-colors duration-300 ${
              filters.status === item.value
                ? "bg-vibrantBlueBg text-white"
                : "bg-warmOffWhiteBg text-mediumGrayText font-bold"
            }`}
            onClick={() => handleTabChange(item.value)}
          >
            {item.title}
          </button>
        ))}
      </div>

      <div className="w-full flex flex-col gap-4">
        <div className="bg-white rounded-t-lg  border border-mediumGrayBorder overflow-x-auto">
          <table className="min-w-full divide-y divide-mediumGrayBorder">
            {/* Table Header */}
            <thead className="bg-white">
              <tr className="text-left">
                {columns.map((column, index) => {
                  return (
                    <th
                      className="px-4 py-4 text-sm font-bold text-left text-primaryText whitespace-nowrap"
                      key={index}
                    >
                      {column}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white divide-y divide-mediumGrayBorder">
              {data.map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 ">
                  <td className="px-4 py-4 text-sm  text-primaryText font-semibold whitespace-nowrap">
                    {index + 1}
                  </td>
                  <td className="px-4 py-4 text-sm  text-primaryText font-semibold whitespace-nowrap">
                    {item?.ambassador?.user?.firstName}{" "}
                    {item?.ambassador?.user?.lastName}
                  </td>
                  <td className="px-4 py-4 text-sm  text-primaryText font-semibold whitespace-nowrap">
                    {item?.totalItems || "--"}
                  </td>

                  <td className="px-4 py-4 text-sm  text-primaryText font-semibold whitespace-nowrap">
                    {item?.deliveryAddress || "-"}
                  </td>

                  <td className="px-4 py-4 text-left whitespace-nowrap w-[124px]">
                    {/* Action Buttons */}

                    <div>
                      {hasPermission(sitePermissions.orderRequestsRead) ? (
                        <button className="">
                          <EyeIcon
                            onClick={() => goToDetail(item)}
                            className="w-[28px] h-[28px]"
                          />
                        </button>
                      ) : (
                        <span className="cursor-not-allowed">
                          <EyeIcon className="w-[28px] h-[28px]" />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Fragment>
          <DataNotFound show={!isLoading && !data.length} />
          {totalElements > 0 && (
            <Pagination
              count={totalElements}
              page={filters.page}
              limit={filters.limit}
              onPageChange={onPageChange}
            />
          )}
        </Fragment>
      </div>
    </div>
  );
};

export default OrderRequests;
